import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    storageKey: { type: String, default: "lumenTheme" },
  }

  static targets = ["radio"]

  declare storageKeyValue: string
  declare readonly radioTargets: HTMLInputElement[]
  declare readonly hasRadioTarget: boolean

  private mediaQuery: MediaQueryList | null = null
  private systemThemeListener: ((e: MediaQueryListEvent) => void) | null = null
  private radioGroupName: string | null = null

  connect() {
    this.ensureUniqueRadioGroup()

    // Load and apply theme immediately, then setup listeners
    this.loadAndApplyTheme()
    this.setupListeners()
  }

  disconnect() {
    this.removeSystemThemeListener()
  }

  /**
   * Load theme from localStorage and apply it
   */
  private loadAndApplyTheme() {
    const savedTheme = this.getSavedTheme()
    this.applyTheme(savedTheme)
  }

  /**
   * Get saved theme from localStorage, defaults to "system"
   */
  private getSavedTheme(): "light" | "dark" | "system" {
    const saved = localStorage.getItem(this.storageKeyValue)
    if (saved === "light" || saved === "dark" || saved === "system") {
      return saved
    }
    return "system"
  }

  /**
   * Apply theme to the document
   */
  private applyTheme(theme: "light" | "dark" | "system") {
    const resolvedTheme = this.resolveTheme(theme)
    const themeName = resolvedTheme === "dark" ? "lumenfall-dark" : "lumenfall"

    document.documentElement.setAttribute("data-theme", themeName)

    // Update the theme selector state if it exists
    this.updateThemeSelector(theme)
  }

  /**
   * Resolve "system" theme to actual light/dark value
   */
  private resolveTheme(theme: "light" | "dark" | "system"): "light" | "dark" {
    if (theme === "system") {
      return this.getSystemTheme()
    }
    return theme
  }

  /**
   * Get system theme preference
   */
  private getSystemTheme(): "light" | "dark" {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark"
    }
    return "light"
  }

  /**
   * Setup listener for system theme changes
   */
  private setupSystemThemeListener() {
    if (!window.matchMedia) return

    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.systemThemeListener = () => {
      const currentTheme = this.getSavedTheme()
      // Only react to system theme changes if user has "system" selected
      if (currentTheme === "system") {
        this.applyTheme("system")
      }
    }

    // Modern browsers
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.systemThemeListener)
    }
  }

  /**
   * Remove system theme listener on disconnect
   */
  private removeSystemThemeListener() {
    if (this.mediaQuery && this.systemThemeListener) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener("change", this.systemThemeListener)
      }
    }
  }

  /**
   * Setup listeners for system theme changes
   */
  private setupListeners() {
    this.setupSystemThemeListener()
  }

  /**
   * Update theme selector UI to reflect current theme
   */
  private updateThemeSelector(theme: "light" | "dark" | "system") {
    if (!this.hasRadioTarget) return

    this.radioTargets.forEach((input) => {
      input.checked = input.value === theme
    })
  }

  /**
   * Handle theme change from user interaction
   * Called via data-action from radio inputs
   */
  change(event: Event) {
    const target = event.target as HTMLInputElement
    const theme = target.value as "light" | "dark" | "system"

    if (theme === "light" || theme === "dark" || theme === "system") {
      localStorage.setItem(this.storageKeyValue, theme)
      this.applyTheme(theme)
    }
  }

  /**
   * Ensure each theme selector has its own radio group so that cloned sidebars
   * (desktop + mobile) do not fight over the shared "theme" name attribute.
   */
  private ensureUniqueRadioGroup() {
    if (!this.hasRadioTarget || this.radioGroupName) return

    this.radioGroupName = this.generateRadioGroupName()
    this.radioTargets.forEach((input) => {
      input.name = this.radioGroupName as string
    })
  }

  private generateRadioGroupName(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return `theme-${crypto.randomUUID()}`
    }

    return `theme-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  }
}
